/**
 * Camera capture and client-side image compression.
 *
 * This matters more than it looks. A modern phone camera produces a 4–8 MB
 * JPEG. Uploading that over shop 4G is slow enough that a driver will give up
 * on the POD screen, and chat images are capped at 2 MB server-side anyway.
 *
 * Compressing before upload turns an 8 MB photo into roughly 200–400 KB with
 * no loss of usefulness — a delivery photo needs to show cartons at a shop, not
 * print at A3. It also directly reduces the permanent storage bill, since POD
 * photos are retained for eight years.
 *
 * Uses `createImageBitmap` + OffscreenCanvas where available (fast, off the
 * main thread) and falls back to a plain canvas.
 */

export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
  originalSizeBytes: number;
  sizeBytes: number;
  mimeType: string;
}

export interface CompressOptions {
  /** Longest edge in pixels. 1600 keeps text on a carton readable. */
  maxDimension?: number;
  /** JPEG quality 0–1. */
  quality?: number;
  /** Hard ceiling; quality is reduced until the blob fits. */
  maxBytes?: number;
}

const DEFAULTS: Required<CompressOptions> = {
  maxDimension: 1600,
  quality: 0.82,
  maxBytes: 2 * 1024 * 1024,
};

/**
 * Open the camera (or gallery) and return the chosen file.
 *
 * `capture="environment"` asks Android for the rear camera directly, which
 * skips a gallery step the driver does not need.
 */
export function pickImage(options?: { fromCamera?: boolean; multiple?: boolean }): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';

    /**
     * `image/*` WHEN WE WANT THE CAMERA, A NARROW LIST OTHERWISE.
     *
     * This always sent `image/jpeg,image/png,image/webp`, and Chrome on Android
     * IGNORES `capture` when `accept` is a specific list — it falls back to the
     * full picker, gallery and all. So the driver was offered his photo library
     * when the whole point was a photograph taken at the door, and a delivery
     * could be evidenced with a picture from last week.
     *
     * `image/*` is what makes `capture` bite. The narrow list stays for the
     * non-camera case, where picking an existing file is the intent; the server
     * validates the real type on upload either way, so nothing here is a
     * security control.
     */
    input.accept = options?.fromCamera ? 'image/*' : 'image/jpeg,image/png,image/webp';
    if (options?.fromCamera) input.setAttribute('capture', 'environment');
    if (options?.multiple) input.multiple = true;
    input.style.display = 'none';

    // Resolve with an empty array if the user backs out, so callers can simply
    // check length rather than handling a never-settling promise.
    let settled = false;
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(files);
    };

    input.addEventListener('change', () => finish(Array.from(input.files ?? [])));
    input.addEventListener('cancel', () => finish([]));

    /**
     * THE FOCUS FALLBACK USED TO THROW AWAY REAL PHOTOS.
     *
     * `cancel` is not universally supported, so regaining focus is the only
     * signal that the picker closed with nothing. The previous version waited
     * 600 ms after focus and then settled empty.
     *
     * On Android `change` frequently fires AFTER focus returns, and a
     * twelve-megapixel photo takes longer than 600 ms to materialise into a
     * File. The timer won, the promise settled with no files, and the real
     * `change` was then discarded because `settled` was already true. The
     * driver chose a photo and the app behaved as though he had cancelled —
     * reported on 2 Oct as "it is not accepting".
     *
     * Now focus starts a POLL rather than a verdict: the moment files appear
     * they are used, and empty is concluded only after ten seconds of nothing.
     * The cost of waiting is a few hundred milliseconds on a genuine cancel.
     * The cost of guessing was a lost delivery photo.
     */
    window.addEventListener('focus', () => {
      const startedAt = Date.now();
      const poll = window.setInterval(() => {
        if (settled) { window.clearInterval(poll); return; }
        if (input.files?.length) {
          window.clearInterval(poll);
          finish(Array.from(input.files));
          return;
        }
        if (Date.now() - startedAt > 10_000) {
          window.clearInterval(poll);
          finish([]);
        }
      }, 250);
    }, { once: true });

    document.body.appendChild(input);
    input.click();
  });
}

/** Resize and re-encode an image, stepping quality down until it fits maxBytes. */
export async function compress(file: File | Blob, options?: CompressOptions): Promise<CompressedImage> {
  const opts = { ...DEFAULTS, ...options };
  const originalSizeBytes = file.size;

  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, opts.maxDimension / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(width, height)
    : Object.assign(document.createElement('canvas'), { width, height });

  const ctx = (canvas as OffscreenCanvas | HTMLCanvasElement).getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('Canvas is not available');

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  let quality = opts.quality;
  let blob = await toBlob(canvas, quality);

  // Step quality down rather than dimensions: a slightly softer photo is more
  // useful than a smaller one when the point is to show what was delivered.
  while (blob.size > opts.maxBytes && quality > 0.4) {
    quality -= 0.12;
    blob = await toBlob(canvas, quality);
  }

  return {
    blob,
    width,
    height,
    originalSizeBytes,
    sizeBytes: blob.size,
    mimeType: 'image/jpeg',
  };
}

async function toBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not encode image'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Capture from the camera and compress in one step. */
export async function capturePhoto(options?: CompressOptions): Promise<CompressedImage | null> {
  const [file] = await pickImage({ fromCamera: true });
  if (!file) return null;
  return compress(file, options);
}

/** A short-lived object URL for previewing a blob. Revoke when the view unmounts. */
export function previewUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

export function revokePreview(url: string): void {
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Geolocation
// ---------------------------------------------------------------------------

export interface Coords {
  latitude: number;
  longitude: number;
  accuracyM: number;
}

/**
 * Read the device GPS once.
 *
 * Used at customer registration (to store the shop pin) and at POD. It is NOT
 * used for attendance — the client excluded presence tracking, and no location
 * is attached to a day session.
 *
 * Resolves null rather than throwing when permission is denied: a driver with
 * location switched off must still be able to complete a delivery.
 */
export function getCoords(options?: { timeoutMs?: number; highAccuracy?: boolean }): Promise<Coords | null> {
  if (!navigator.geolocation) return Promise.resolve(null);

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: Math.round(pos.coords.latitude * 1e6) / 1e6,
        longitude: Math.round(pos.coords.longitude * 1e6) / 1e6,
        accuracyM: Math.round(pos.coords.accuracy),
      }),
      () => resolve(null),
      {
        enableHighAccuracy: options?.highAccuracy ?? true,
        timeout: options?.timeoutMs ?? 10_000,
        // A pin from the last half-minute is fine and avoids a cold GPS fix,
        // which can take 30 seconds indoors.
        maximumAge: 30_000,
      },
    );
  });
}
